import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useLink } from '@grandvisionhq/frk-routing';
import { Hooks, ExpressPaymentMethod } from '@grandvisionhq/payments';
import { useSessionStorage } from '@grandvisionhq/utils';
import { useApplePay } from './apple-pay/use-apple-pay.js';
import { useGooglePay } from './google-pay/use-google-pay.js';

const useHandleExpressPayment = (type1, returnPath)=>{
    const [payCartWithApplePay, { loading: applePayOrderInProcess  }] = useApplePay({
        returnPath
    });
    const [payCartWithGooglePay, { loading: gpayOrderInProcess  }] = useGooglePay({
        returnPath
    });
    const { onClick: redirectToHomePage  } = useLink({
        route: 'home'
    }, {
        replace: true
    }) || {};
    const { onClick: redirectToCartPage  } = useLink({
        route: 'cart.overview'
    }, {
        replace: true
    }) || {};
    const [paymentConfiguration] = useSessionStorage('payment-configuration');
    const { isApplePaySupportedForCart , called: isApplePaySupportedForCartCalled , loading: isApplePaySupportedForCartLoading ,  } = Hooks.useIsApplePaySupportedForCart();
    const { isGooglePaySupportedForCart , called: isGooglePaySupportedForCartCalled , loading: isGooglePaySupportedForCartLoading ,  } = Hooks.useIsGooglePaySupportedForCart();
    const handleExpressPaymentMethodValidity = React.useCallback((type)=>{
        if (type === ExpressPaymentMethod.GOOGLE_PAY) {
            if (!paymentConfiguration) {
                console.error(`useHandleExpressPayment: no configuration found for Google Pay`);
                toast.failed('cart.errors.somethingWentWrong');
                // If the payment configuration is missing, redirect the user back to the cart page
                redirectToCartPage === null || redirectToCartPage === void 0 ? void 0 : redirectToCartPage();
            }
            if (!isGooglePaySupportedForCart && isGooglePaySupportedForCartCalled && !isGooglePaySupportedForCartLoading) {
                console.error(`useHandleExpressPayment: Google Pay is not supported or not enabled`);
                redirectToHomePage === null || redirectToHomePage === void 0 ? void 0 : redirectToHomePage();
            }
        } else if (type === ExpressPaymentMethod.APPLE_PAY) {
            if (!isApplePaySupportedForCart && isApplePaySupportedForCartCalled && !isApplePaySupportedForCartLoading) {
                console.error(`useHandleExpressPayment: ApplePay is not supported or not enabled`);
                redirectToHomePage === null || redirectToHomePage === void 0 ? void 0 : redirectToHomePage();
            }
        }
    }, [
        paymentConfiguration,
        isGooglePaySupportedForCart,
        isGooglePaySupportedForCartCalled,
        isGooglePaySupportedForCartLoading,
        redirectToCartPage,
        redirectToHomePage,
        isApplePaySupportedForCart,
        isApplePaySupportedForCartCalled,
        isApplePaySupportedForCartLoading, 
    ]);
    React.useEffect(()=>{
        handleExpressPaymentMethodValidity(type1);
    // Excluded handleExpressPaymentMethodValidity from dep to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        type1
    ]);
    const getExpressPaymentConfig = React.useCallback((type)=>{
        switch(type){
            case ExpressPaymentMethod.GOOGLE_PAY:
                return payCartWithGooglePay;
            case ExpressPaymentMethod.APPLE_PAY:
                return payCartWithApplePay;
            default:
                redirectToCartPage === null || redirectToCartPage === void 0 ? void 0 : redirectToCartPage();
                console.error(`Not implemented yet: ${type} case`);
        }
        return undefined;
    }, [
        payCartWithApplePay,
        payCartWithGooglePay,
        redirectToCartPage
    ]);
    return React.useMemo(()=>[
            getExpressPaymentConfig(type1),
            gpayOrderInProcess || applePayOrderInProcess
        ]
    , [
        applePayOrderInProcess,
        getExpressPaymentConfig,
        gpayOrderInProcess,
        type1
    ]);
};

export { useHandleExpressPayment };
//# sourceMappingURL=use-handle-express-payment.js.map
